import { useCallback, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Badge,
  Banner,
  Button,
  Card,
  DataList,
  FieldSelect,
  FieldTextarea,
  LoadingAnimation,
  Modal,
  Stack,
  Table,
  type TableColumn,
  Tabs,
  useToast,
} from "@knowtex-ai/components";
import type { McIssue, McIssueOccurrence, McIssueStatus } from "../../lib/mc";
import {
  useMcIssueQuery,
  useMcIssuesQuery,
  useUpdateMcIssue,
} from "../../hooks/useMcRuns";
import ClampedText from "./ClampedText";
import {
  countOf,
  formatCount,
  formatDateTime,
  issueRef,
  ISSUE_STATUS_BADGES,
  ISSUE_STATUS_ORDER,
  severityVariant,
} from "./format";
import McHeader from "./McHeader";
import McPageShell from "./McPageShell";
import { GAP } from "./layout";
import NewTabLink from "./NewTabLink";
import TimeCell from "./TimeCell";
import { useRetained } from "./useRetained";

interface McIssuesPageProps {
  setAuth: (isAuthenticated: boolean) => void;
  setUserOrg: (org: string | null) => void;
}

const MAX_NOTE_CHARS = 4000;

type TabId = "triage" | "confirmed" | "fixed" | "dismissed" | "all";

const TABS: {
  id: TabId;
  label: string;
  statuses?: McIssueStatus[];
  empty: string;
}[] = [
  {
    id: "triage",
    label: "Triage required",
    statuses: ["new", "regressed"],
    empty: "Nothing needs triage.",
  },
  {
    id: "confirmed",
    label: "Confirmed",
    statuses: ["confirmed"],
    empty: "No confirmed issues.",
  },
  {
    id: "fixed",
    label: "Fixed",
    statuses: ["fixed"],
    empty: "Nothing marked fixed.",
  },
  {
    id: "dismissed",
    label: "Dismissed",
    statuses: ["false_positive", "wont_fix"],
    empty: "Nothing dismissed.",
  },
  { id: "all", label: "All", empty: "MC hasn't found any issues yet." },
];

export const issueUrl = (issueId: number) => `/mc/issues?issue=${issueId}`;

/** Opens the finding on its run's page. */
export const findingUrl = (runId: string, findingId: string) =>
  `/mc/runs/${runId}?finding=${encodeURIComponent(findingId)}`;

export function IssueStatusBadge({ status }: { status: McIssueStatus }) {
  const spec = ISSUE_STATUS_BADGES[status] ?? {
    label: status,
    variant: "default",
  };
  return (
    <Badge size="small" variant={spec.variant} tip={spec.tip}>
      {spec.label}
    </Badge>
  );
}

/** Set an issue's status and note. Later runs see both. */
export function IssueTriageForm({
  issue,
}: {
  issue: Pick<McIssue, "id" | "status" | "note" | "updatedBy" | "updatedAt">;
}) {
  const toast = useToast();
  const update = useUpdateMcIssue();
  const [status, setStatus] = useState<McIssueStatus>(issue.status);
  const [note, setNote] = useState(issue.note ?? "");
  const changed = status !== issue.status || note.trim() !== (issue.note ?? "");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    update.mutate(
      {
        id: issue.id,
        ...(status !== issue.status && { status }),
        ...(note.trim() !== (issue.note ?? "") && { note: note.trim() }),
      },
      { onSuccess: () => toast.success(`${issueRef(issue.id)} updated`) },
    );
  };

  return (
    <Card variant="elevated" size="small">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <FieldSelect
          label="Status"
          size="small"
          items={ISSUE_STATUS_ORDER.map((value) => ({
            value,
            label: ISSUE_STATUS_BADGES[value].label,
          }))}
          value={status}
          onChange={(value) => value && setStatus(value as McIssueStatus)}
          disabled={update.isPending}
          triggerMinWidth="100%"
        />
        <FieldTextarea
          label="Note"
          placeholder="e.g. Tracked in KX-123, or why this is expected"
          description="Runs see the note next to the issue. They stop reporting dismissed issues (false positives and won't fix), and a fixed issue that shows up again is marked as a regression."
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE_CHARS))}
          maxLength={MAX_NOTE_CHARS}
          autoComplete="off"
          rows={2}
          size="small"
          disabled={update.isPending}
        />
        {update.isError && (
          <Banner variant="error" heading="Couldn't save">
            {update.error.message}
          </Banner>
        )}
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] text-gray-500">
            {issue.updatedBy && issue.updatedBy !== "mc"
              ? `Updated by ${issue.updatedBy} on ${formatDateTime(issue.updatedAt)}`
              : "Not triaged by anyone yet"}
          </span>
          <Button
            type="submit"
            size="small"
            loading={update.isPending}
            disabled={!changed}
          >
            Save
          </Button>
        </div>
      </form>
    </Card>
  );
}

const occurrenceColumns: TableColumn<McIssueOccurrence>[] = [
  {
    id: "at",
    header: "Seen",
    width: 200,
    accessor: (o) => new Date(o.at),
    cell: (o) => <TimeCell iso={o.at} />,
  },
  {
    id: "run",
    header: "Run",
    width: 180,
    accessor: "runId",
    cell: (o) => (
      <NewTabLink
        to={findingUrl(o.runId, o.findingId)}
        aria-label={`Open finding ${o.findingId} in MC run ${o.runId}`}
      >
        <span className="font-mono">
          {o.runId.slice(0, 8)}#{o.findingId}
        </span>
      </NewTabLink>
    ),
  },
  {
    id: "title",
    header: "As reported",
    width: 360,
    grow: true,
    accessor: "title",
    cell: (o) => <ClampedText text={o.title} lines={2} />,
  },
];

function IssueModal({
  issueId,
  onClose,
}: {
  issueId: number | null;
  onClose: () => void;
}) {
  const shownId = useRetained(issueId);
  const query = useMcIssueQuery(shownId);
  const issue = query.data?.issue;
  return (
    <Modal
      open={issueId !== null}
      onOpenChange={(open) => !open && onClose()}
      title={shownId ? `Manage issue ${issueRef(shownId)}` : "Manage issue"}
      size="large"
    >
      {query.isLoading ? (
        <div className="flex justify-center py-12 text-gray-400">
          <LoadingAnimation size={24} />
        </div>
      ) : !issue ? (
        <Banner variant="error" heading="Couldn't load the issue">
          {query.error?.message}
        </Banner>
      ) : (
        <div className="flex flex-col gap-5">
          <IssueTriageForm
            key={`${issue.id}:${issue.updatedAt}`}
            issue={issue}
          />
          <DataList
            size="small"
            aria-label={`Issue ${issueRef(issue.id)}`}
            items={[
              {
                id: "id",
                label: "ID",
                value: <span className="font-mono">{issueRef(issue.id)}</span>,
              },
              {
                id: "title",
                label: "Issue",
                value: (
                  <p className="whitespace-pre-wrap break-words">
                    {issue.title}
                  </p>
                ),
              },
              {
                id: "status",
                label: "Status",
                value: <IssueStatusBadge status={issue.status} />,
              },
              {
                id: "severity",
                label: "Severity",
                value: issue.severity ? (
                  <Badge size="small" variant={severityVariant(issue.severity)}>
                    {issue.severity}
                  </Badge>
                ) : (
                  "—"
                ),
              },
              {
                id: "kind",
                label: "Kind",
                value: issue.kind ? (
                  <span className="font-mono">{issue.kind}</span>
                ) : (
                  "—"
                ),
              },
              {
                id: "seen",
                label: "Reported",
                value: countOf(issue.seenCount, "time"),
              },
            ]}
          />
          <Table
            aria-label={`Runs that reported ${issueRef(issue.id)}`}
            size="small"
            columns={occurrenceColumns}
            data={query.data?.occurrences ?? []}
            getRowId={(o) => `${o.runId}:${o.findingId}`}
            paginated={false}
            emptyState="No runs linked."
          />
        </div>
      )}
    </Modal>
  );
}

function McIssues() {
  const issuesQuery = useMcIssuesQuery();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<TabId>("triage");
  const issues = useMemo(() => issuesQuery.data ?? [], [issuesQuery.data]);
  const openId = Number(params.get("issue")) || null;
  const open = useCallback(
    (id: number | null) =>
      setParams(id ? { issue: String(id) } : {}, { replace: true }),
    [setParams],
  );
  const inTab = (t: (typeof TABS)[number]) =>
    t.statuses ? issues.filter((i) => t.statuses!.includes(i.status)) : issues;
  const current = TABS.find((t) => t.id === tab)!;

  const columns = useMemo(
    (): TableColumn<McIssue>[] => [
      {
        id: "id",
        header: "ID",
        width: 80,
        accessor: "id",
        cell: (i) => <span className="font-mono">{issueRef(i.id)}</span>,
      },
      {
        id: "status",
        header: "Status",
        width: 140,
        accessor: (i) => ISSUE_STATUS_ORDER.indexOf(i.status),
        sortable: true,
        cell: (i) => <IssueStatusBadge status={i.status} />,
      },
      {
        id: "severity",
        header: "Severity",
        width: 110,
        accessor: "severity",
        cell: (i) =>
          i.severity ? (
            <Badge size="small" variant={severityVariant(i.severity)}>
              {i.severity}
            </Badge>
          ) : (
            "—"
          ),
      },
      {
        id: "title",
        header: "Issue",
        width: 420,
        grow: true,
        accessor: "title",
        cell: (i) => (
          <div className="min-w-0">
            <ClampedText text={i.title} lines={2} />
            {i.note && (
              <p className="truncate text-[13px] text-gray-500" title={i.note}>
                {i.note}
              </p>
            )}
          </div>
        ),
      },
      {
        id: "seen",
        header: "Reported",
        width: 100,
        accessor: "seenCount",
        sortable: true,
        cell: (i) => `${formatCount(i.seenCount)}×`,
      },
      {
        id: "lastSeen",
        header: "Last seen",
        width: 200,
        accessor: (i) => new Date(i.lastSeen?.at ?? i.createdAt),
        sortable: true,
        cell: (i) => <TimeCell iso={i.lastSeen?.at ?? i.createdAt} />,
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        width: 88,
        sticky: "end",
        hideable: false,
        cell: (i) => (
          <Button
            variant="secondary"
            size="small"
            onClick={() => open(i.id)}
            aria-label={`View issue ${issueRef(i.id)}`}
          >
            View
          </Button>
        ),
      },
    ],
    [open],
  );

  const body = () => {
    if (issuesQuery.isLoading) {
      return (
        <div className="flex justify-center py-24 text-gray-400">
          <LoadingAnimation size={28} />
        </div>
      );
    }
    if (issuesQuery.isError) {
      return (
        <Banner variant="error" heading="Couldn't load issues">
          {issuesQuery.error.message}
        </Banner>
      );
    }
    return (
      <Stack gap={GAP.page}>
        <Banner variant="brand" heading="How issues work">
          Each problem MC finds becomes one issue, however many runs report it.
          Runs see every issue and its status, so they keep reporting new and
          confirmed issues and skip dismissed ones. A fixed issue that shows up
          again is marked as a regression.
        </Banner>
        <Stack gap={GAP.block}>
          <Tabs
            aria-label="Issue status"
            size="small"
            activeTab={tab}
            tabs={TABS.map((t) => ({
              id: t.id,
              label: t.label,
              count: inTab(t).length,
              onClick: () => setTab(t.id),
            }))}
          />
          <Table
            aria-label={`${current.label} issues`}
            columns={columns}
            data={inTab(current)}
            getRowId={(i) => String(i.id)}
            defaultPageSize={50}
            emptyState={current.empty}
          />
        </Stack>
      </Stack>
    );
  };

  return (
    <>
      <McHeader section="issues" />
      <div className="mt-8">{body()}</div>
      <IssueModal issueId={openId} onClose={() => open(null)} />
    </>
  );
}

export default function McIssuesPage({
  setAuth,
  setUserOrg,
}: McIssuesPageProps) {
  return (
    <McPageShell setAuth={setAuth} setUserOrg={setUserOrg}>
      <McIssues />
    </McPageShell>
  );
}
